import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { AlertCircle, Copy, Plus, RefreshCw, Send, Trash2, Webhook, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { EntityList, type EntityListColumn } from '@/components/crm/entity-list'
import { useServerList } from '@/components/data-table/use-server-list'
import { PageHeader } from '@/components/layout/page-header'
import { useEntityListNav } from '@/components/layout/use-entity-nav'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { errorMessage } from '@/lib/error-message'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/custom-modules/$key/webhooks')({
  component: WebhooksPage,
})

interface WebhookRow {
  id: string
  name: string
  url: string
  eventPattern: string | null
  enabled: boolean
  secretPreview: string
}

function WebhooksPage() {
  const { key: moduleKey } = Route.useParams()
  const t = useTranslations()
  const queryClient = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [newSecret, setNewSecret] = useState<{ id: string; secret: string } | null>(null)

  // Zoeken, sorteren en pagineren gebeuren in de database; deze pagina rendert wat
  // er terugkomt. De module is de afbakening en reist mee in de invoer. Er is geen
  // recordnavigatie: webhooks zijn dialoog-CRUD zonder detailpagina, dus de router
  // heeft ook geen `neighbors` (zie custom-webhooks.router.ts).
  const filters = useMemo(() => ({ moduleKey }), [moduleKey])
  const list = useServerList({
    entityKey: 'customWebhook',
    initialSorting: [{ id: 'name', desc: false }],
    filters,
  })
  const webhooksQ = useQuery(
    orpc.modules.custom.webhooks.list.queryOptions({ input: { ...list.input, ...filters } }),
  )
  const rows = useMemo<WebhookRow[]>(
    () =>
      (webhooksQ.data?.rows ?? []).map((wh) => ({
        id: wh.id,
        name: wh.name,
        url: wh.url,
        eventPattern: wh.eventPattern,
        enabled: wh.enabled,
        secretPreview: wh.secretPreview,
      })),
    [webhooksQ.data],
  )
  const total = webhooksQ.data?.total
  useEffect(() => {
    list.setTotal(total)
  }, [total, list.setTotal])

  useEntityListNav({
    entityKey: 'customWebhook',
    total: total ?? 0,
    toNew: () => setCreateOpen(true),
    search: list.search,
    onSearch: list.setSearch,
    sort: list.sort,
  })

  const deleteMutation = useMutation(
    orpc.modules.custom.webhooks.delete.mutationOptions({
      onSuccess: () => {
        toast.success(t('builderRuntime.webhooks.toast.deleted'))
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.webhooks.list.queryKey({
            input: { moduleKey },
          }),
        })
      },
      onError: (err) => toast.error(errorMessage(err, t)),
    }),
  )

  const testMutation = useMutation(
    orpc.modules.custom.webhooks.test.mutationOptions({
      onSuccess: () => toast.success(t('builderRuntime.webhooks.toast.testEnqueued')),
      onError: (err) => toast.error(errorMessage(err, t)),
    }),
  )

  const rotateMutation = useMutation(
    orpc.modules.custom.webhooks.update.mutationOptions({
      onSuccess: (webhook) => {
        if (webhook.secret) {
          setNewSecret({ id: webhook.id, secret: webhook.secret })
        }
        toast.success(t('builderRuntime.webhooks.toast.secretRotated'))
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.webhooks.list.queryKey({
            input: { moduleKey },
          }),
        })
      },
      onError: (err) => toast.error(errorMessage(err, t)),
    }),
  )

  // Elke kolom met een sorteerpijl (`sortAccessor`) staat ook in `sortColumns` van
  // custom-webhooks.router.ts — in servermodus gaat een kopklik regelrecht naar
  // `input.sort`, dus een kolom die daar ontbreekt zou het verzoek afkeuren. De
  // actiekolom heeft er bewust geen.
  const columns = useMemo<EntityListColumn<WebhookRow>[]>(
    () => [
      {
        key: 'name',
        header: t('builderRuntime.webhooks.col.name'),
        render: (r) => <span className="font-medium">{r.name}</span>,
        sortAccessor: (r) => r.name,
      },
      {
        key: 'url',
        header: t('builderRuntime.webhooks.col.url'),
        render: (r) => (
          <span className="font-mono text-xs text-muted-foreground truncate block max-w-md">
            {r.url}
          </span>
        ),
        sortAccessor: (r) => r.url,
      },
      {
        key: 'eventPattern',
        header: t('builderRuntime.webhooks.col.event'),
        render: (r) => (
          <span className="font-mono text-xs text-muted-foreground">{r.eventPattern ?? '*'}</span>
        ),
        sortAccessor: (r) => r.eventPattern,
      },
      {
        key: 'enabled',
        header: t('builderRuntime.webhooks.col.status'),
        render: (r) =>
          r.enabled ? (
            <Badge variant="default">{t('builderRuntime.webhooks.status.enabled')}</Badge>
          ) : (
            <Badge variant="secondary">{t('builderRuntime.webhooks.status.disabled')}</Badge>
          ),
        sortAccessor: (r) => String(r.enabled),
      },
      {
        key: 'actions',
        header: null,
        className: 'text-right',
        render: (r) => (
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => testMutation.mutate({ id: r.id })}
              disabled={testMutation.isPending}
            >
              <Send className="h-4 w-4 mr-1" />
              {t('builderRuntime.webhooks.test')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => rotateMutation.mutate({ id: r.id, rotateSecret: true })}
              disabled={rotateMutation.isPending}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              {t('builderRuntime.webhooks.rotate')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => deleteMutation.mutate({ id: r.id })}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [t, testMutation, rotateMutation, deleteMutation],
  )

  return (
    <>
      <PageHeader
        title={t('builderRuntime.webhooks.title')}
        subtitle={t('builderRuntime.webhooks.subtitle')}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('builderRuntime.webhooks.addAction')}
          </Button>
        }
      />

      <EntityList<WebhookRow>
        rows={rows}
        columns={columns}
        isLoading={webhooksQ.isLoading}
        emptyState={
          <div className="flex flex-col items-center gap-2">
            <Webhook className="h-8 w-8 text-muted-foreground opacity-40" />
            <h3 className="font-medium">{t('builderRuntime.webhooks.empty.title')}</h3>
            <p className="text-sm text-muted-foreground max-w-md">
              {t('builderRuntime.webhooks.empty.subtitle')}
            </p>
            <Button className="mt-2" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t('builderRuntime.webhooks.empty.cta')}
            </Button>
          </div>
        }
        server={list.server}
      />

      <CreateWebhookDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        moduleKey={moduleKey}
        onCreated={(created) => {
          setNewSecret({ id: created.id, secret: created.secret })
          setCreateOpen(false)
        }}
      />

      {newSecret && <SecretBanner secret={newSecret.secret} onDismiss={() => setNewSecret(null)} />}
    </>
  )
}

function SecretBanner({ secret, onDismiss }: { secret: string; onDismiss: () => void }) {
  const t = useTranslations()
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    void navigator.clipboard.writeText(secret).then(() => setCopied(true))
  }
  return (
    <div className="fixed bottom-4 right-4 max-w-md rounded-md border border-amber-500/50 bg-amber-50 dark:bg-amber-950 p-4 shadow-lg">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm">{t('builderRuntime.webhooks.secret.title')}</p>
          <p className="text-xs text-muted-foreground mb-2">
            {t('builderRuntime.webhooks.secret.body')}
          </p>
          <code className="block font-mono text-xs bg-background rounded border px-2 py-1 break-all">
            {secret}
          </code>
          <div className="flex gap-2 mt-2">
            <Button size="sm" variant="outline" onClick={handleCopy}>
              <Copy className="h-3 w-3 mr-1" />
              {copied
                ? t('builderRuntime.webhooks.secret.copied')
                : t('builderRuntime.webhooks.secret.copy')}
            </Button>
            <Button size="sm" variant="ghost" onClick={onDismiss}>
              <X className="h-3 w-3 mr-1" />
              {t('builderRuntime.webhooks.secret.dismiss')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function CreateWebhookDialog({
  open,
  onClose,
  moduleKey,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  moduleKey: string
  onCreated: (created: { id: string; secret: string }) => void
}) {
  const t = useTranslations()
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const queryClient = useQueryClient()

  const createMutation = useMutation(
    orpc.modules.custom.webhooks.create.mutationOptions({
      onSuccess: (webhook) => {
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.webhooks.list.queryKey({
            input: { moduleKey },
          }),
        })
        onCreated({ id: webhook.id, secret: webhook.secret })
        setName('')
        setUrl('')
      },
      onError: (err) => toast.error(errorMessage(err, t)),
    }),
  )

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('builderRuntime.webhooks.addAction')}</DialogTitle>
          <DialogDescription>{t('builderRuntime.webhooks.dialog.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="wh-name">{t('builderRuntime.webhooks.col.name')}</Label>
            <Input
              id="wh-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('builderRuntime.webhooks.dialog.namePlaceholder')}
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="wh-url">{t('builderRuntime.webhooks.col.url')}</Label>
            <Input
              id="wh-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://hooks.slack.com/services/…"
              className="font-mono text-xs"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!name || !url || createMutation.isPending}
            onClick={() =>
              createMutation.mutate({
                moduleKey,
                name,
                url,
              })
            }
          >
            {createMutation.isPending ? t('common.creating') : t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
